import { collection, addDoc, serverTimestamp, Timestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { ESTADOS_EMPRESA } from '../utils/empresaStandards';

/**
 * Servicio para generar productos y campañas de prueba para una empresa específica
 */
class GenerarDatosPruebaService {
  // Marcas comunes para asignar a empresas
  static marcasComunes = [
    'Toyota', 'Chevrolet', 'Ford', 'Honda', 'Nissan', 
    'Hyundai', 'Kia', 'Mazda', 'Suzuki', 'Mitsubishi'
  ];
  // Datos de productos predefinidos por categoría
  static productosPorCategoria = {
    'Repuestos': [
      { titulo: 'Filtro de Aceite Premium', precio: 8900, cantidad: 50 },
      { titulo: 'Pastillas de Freno Delanteras', precio: 15900, cantidad: 30 },
      { titulo: 'Amortiguador Trasero Original', precio: 45000, cantidad: 15 },
      { titulo: 'Batería 60Ah 12V', precio: 65000, cantidad: 20 },
      { titulo: 'Filtro de Aire', precio: 12000, cantidad: 40 },
      { titulo: 'Bujías Iridium x4', precio: 22000, cantidad: 25 },
      { titulo: 'Correa de Distribución', precio: 35000, cantidad: 12 },
      { titulo: 'Radiador Aluminio', precio: 85000, cantidad: 8 }
    ],
    'Aceites y Lubricantes': [
      { titulo: 'Aceite Motor 5W-30 Sintético 5L', precio: 28900, cantidad: 60 },
      { titulo: 'Aceite Motor 10W-40 Semi Sintético 4L', precio: 18900, cantidad: 45 },
      { titulo: 'Aceite de Transmisión ATF', precio: 15900, cantidad: 30 },
      { titulo: 'Lubricante de Frenos DOT 4', precio: 8900, cantidad: 40 },
      { titulo: 'Aceite Motor 15W-40 Mineral 5L', precio: 12900, cantidad: 50 }
    ],
    'Neumáticos': [
      { titulo: 'Neumático 175/65R14', precio: 45000, cantidad: 20 },
      { titulo: 'Neumático 185/60R15', precio: 52000, cantidad: 18 },
      { titulo: 'Neumático 195/55R16', precio: 65000, cantidad: 15 },
      { titulo: 'Neumático 205/50R17', precio: 78000, cantidad: 12 },
      { titulo: 'Neumático Todo Terreno 235/75R15', precio: 95000, cantidad: 10 }
    ],
    'Servicios': [
      { titulo: 'Cambio de Aceite y Filtro', precio: 25000, cantidad: 999 },
      { titulo: 'Alineación y Balanceo', precio: 35000, cantidad: 999 },
      { titulo: 'Revisión Técnica Completa', precio: 45000, cantidad: 999 },
      { titulo: 'Servicio de Frenos Completo', precio: 55000, cantidad: 999 },
      { titulo: 'Lavado Premium', precio: 12000, cantidad: 999 },
      { titulo: 'Diagnóstico Computarizado', precio: 30000, cantidad: 999 }
    ],
    'Accesorios': [
      { titulo: 'Cargador USB para Auto', precio: 8900, cantidad: 35 },
      { titulo: 'Cable Auxiliar 3.5mm', precio: 5900, cantidad: 50 },
      { titulo: 'Cubrevolante Universal', precio: 12900, cantidad: 25 },
      { titulo: 'Alfombrillas Premium', precio: 18900, cantidad: 30 },
      { titulo: 'Porta Celular Magnético', precio: 7900, cantidad: 40 }
    ]
  };

  // Títulos de campañas
  static titulosCampañas = [
    'Descuento Especial en Repuestos',
    'Promoción de Verano - Hasta 30% OFF',
    'Aceites y Lubricantes con Descuento',
    'Oferta Especial en Neumáticos',
    'Servicios Premium con Descuento',
    'Fin de Mes - Grandes Ofertas',
    'Día del Cliente - Descuentos Especiales',
    'Promoción de Invierno',
    'Productos Destacados del Mes',
    'Oferta Relámpago - Solo Hoy'
  ];

  static descripcionesCampañas = [
    'Aprovecha nuestros mejores precios en repuestos de calidad. Stock limitado.',
    'Gran promoción de verano con descuentos increíbles en todos nuestros productos.',
    'Aceites y lubricantes de primera calidad con los mejores precios del mercado.',
    'Neumáticos de las mejores marcas con excelentes condiciones de pago.',
    'Servicios premium con profesionales certificados. Calidad garantizada.',
    'Últimos días del mes con ofertas especiales. No te lo pierdas.',
    'Celebramos el día del cliente con descuentos especiales y atención personalizada.',
    'Prepárate para el invierno con nuestras ofertas especiales.',
    'Descubre los productos más destacados del mes con precios especiales.',
    'Oferta relámpago válida solo por hoy. Aprovecha ahora.'
  ];

  /**
   * Generar productos de prueba para una empresa
   * @param {Object} empresa - Datos de la empresa
   * @param {number} cantidadProductos - Cantidad de productos a generar (por defecto 5-8)
   * @returns {Promise<Object>} Resultado con productos creados y errores
   */
  static async generarProductosEmpresa(empresa, cantidadProductos = null) {
    try {
      let productosCreados = 0;
      const errores = [];

      // Asegurar que categorias sea siempre un array válido
      let categorias = ['Repuestos']; // Valor por defecto
      if (empresa.categorias && Array.isArray(empresa.categorias) && empresa.categorias.length > 0) {
        categorias = empresa.categorias;
      } else if (empresa.categoria) {
        categorias = [empresa.categoria];
      }

      // Asegurar que marcas sea siempre un array válido
      let marcas = [];
      if (empresa.marcas && Array.isArray(empresa.marcas) && empresa.marcas.length > 0) {
        marcas = empresa.marcas;
      } else if (empresa.marca) {
        marcas = [empresa.marca];
      }

      // Seleccionar categorías (1-3 categorías)
      const categoriasSeleccionadas = categorias.slice(0, Math.min(3, categorias.length));

      // Determinar cantidad de productos a generar
      const cantidadPorCategoria = cantidadProductos 
        ? Math.ceil(cantidadProductos / categoriasSeleccionadas.length)
        : Math.floor(Math.random() * 3) + 2; // 2-4 productos por categoría

      for (const categoria of categoriasSeleccionadas) {
        // Obtener productos disponibles para esta categoría o usar genéricos
        let productosDisponibles = this.productosPorCategoria[categoria] || this.productosPorCategoria['Repuestos'];

        // Seleccionar productos aleatorios
        const productosSeleccionados = productosDisponibles
          .sort(() => Math.random() - 0.5)
          .slice(0, cantidadPorCategoria);

        for (const productoData of productosSeleccionados) {
          try {
            // Preparar datos del producto
            const productoFinalData = {
              empresaId: empresa.id,
              idEmpresa: empresa.id, // Compatibilidad con ambos campos
              nombre: productoData.titulo || 'Producto',
              titulo: productoData.titulo || 'Producto',
              descripcion: `Producto de calidad para ${categoria}. Disponible en stock.`,
              categoria: categoria,
              categorias: [categoria, ...categorias.filter(c => c !== categoria).slice(0, 1)].filter(Boolean),
              marcas: marcas && marcas.length > 0 ? marcas.slice(0, Math.min(2, marcas.length)) : [],
              precio: productoData.precio || 0,
              cantidad: productoData.cantidad || 0,
              stock: productoData.cantidad || 0,
              estado: 'activo',
              imagenUrl: empresa.logo_url || empresa.logo || '',
              linkUrl: empresa.web || empresa.sitio_web || '',
              calificacion: parseFloat((Math.random() * 2 + 3).toFixed(1)), // 3.0 - 5.0
              fechaCreacion: serverTimestamp(),
              creado: serverTimestamp(),
              visible: true
            };

            // Remover campos undefined
            const productoDataFinal = {};
            Object.keys(productoFinalData).forEach(key => {
              const value = productoFinalData[key];
              if (value !== undefined) {
                productoDataFinal[key] = value;
              }
            });

            await addDoc(collection(db, 'productos'), productoDataFinal);
            productosCreados++;
          } catch (error) {
            console.error(`Error creando producto "${productoData.titulo}":`, error);
            errores.push(`Error en producto "${productoData.titulo}": ${error.message}`);
          }
        }
      }

      return {
        exito: true,
        productosCreados,
        errores
      };
    } catch (error) {
      console.error('Error generando productos:', error);
      return {
        exito: false,
        productosCreados: 0,
        errores: [error.message]
      };
    }
  }

  /**
   * Generar campañas de prueba para una empresa
   * @param {Object} empresa - Datos de la empresa
   * @param {number} cantidadCampañas - Cantidad de campañas a generar (por defecto 1-2)
   * @returns {Promise<Object>} Resultado con campañas creadas y errores
   */
  static async generarCampañasEmpresa(empresa, cantidadCampañas = null) {
    try {
      let campañasCreadas = 0;
      const errores = [];

      const categorias = empresa.categorias && Array.isArray(empresa.categorias) && empresa.categorias.length > 0
        ? empresa.categorias
        : (empresa.categoria ? [empresa.categoria] : ['Repuestos']);
      
      const marcas = empresa.marcas && Array.isArray(empresa.marcas) && empresa.marcas.length > 0
        ? empresa.marcas
        : (empresa.marca ? [empresa.marca] : []);

      // Determinar cantidad de campañas
      const numCampañas = cantidadCampañas || Math.floor(Math.random() * 2) + 1; // 1-2 campañas

      for (let i = 0; i < numCampañas; i++) {
        try {
          // Seleccionar título y descripción aleatorios
          const tituloIndex = Math.floor(Math.random() * this.titulosCampañas.length);
          const titulo = this.titulosCampañas[tituloIndex] || 'Campaña Promocional';
          const descripcion = this.descripcionesCampañas[tituloIndex] || 'Oferta especial de temporada';

          // Fecha de inicio (hoy) - convertir a Timestamp
          const fechaInicio = Timestamp.fromDate(new Date());
          // Fecha de fin (30-60 días desde hoy) - convertir a Timestamp
          const diasDuracion = Math.floor(Math.random() * 31) + 30;
          const fechaFinDate = new Date();
          fechaFinDate.setDate(fechaFinDate.getDate() + diasDuracion);
          const fechaFin = Timestamp.fromDate(fechaFinDate);

          // Preparar datos de la campaña
          const campañaData = {
            empresaId: empresa.id,
            idEmpresa: empresa.id, // Compatibilidad
            nombre: empresa.nombre || 'Campaña',
            email: empresa.email || empresa.email_empresa || '',
            titulo: titulo,
            descripcion: descripcion,
            categorias: categorias.slice(0, Math.min(3, categorias.length)),
            marcas: marcas.slice(0, Math.min(3, marcas.length)),
            fechaInicio: fechaInicio, // Ya es Timestamp
            fechaFin: fechaFin, // Ya es Timestamp
            estado: 'Activa',
            fechaCreacion: serverTimestamp(),
            creada: serverTimestamp(),
            visible: true,
            alcance_comunitario: true
          };

          // Remover campos undefined
          const campañaFinalData = {};
          Object.keys(campañaData).forEach(key => {
            const value = campañaData[key];
            if (value !== undefined) {
              campañaFinalData[key] = value;
            }
          });

          await addDoc(collection(db, 'campanas'), campañaFinalData);
          campañasCreadas++;
        } catch (error) {
          console.error(`Error creando campaña:`, error);
          errores.push(`Error en campaña: ${error.message}`);
        }
      }

      return {
        exito: true,
        campañasCreadas,
        errores
      };
    } catch (error) {
      console.error('Error generando campañas:', error);
      return {
        exito: false,
        campañasCreadas: 0,
        errores: [error.message]
      };
    }
  }

  /**
   * Generar productos y campañas de prueba para una empresa
   * @param {Object} empresa - Datos de la empresa
   * @param {Object} opciones - Opciones de generación
   * @param {number} opciones.cantidadProductos - Cantidad de productos a generar
   * @param {number} opciones.cantidadCampañas - Cantidad de campañas a generar
   * @returns {Promise<Object>} Resultado completo con productos y campañas creadas
   */
  static async generarDatosPruebaEmpresa(empresa, opciones = {}) {
    try {
      const { cantidadProductos, cantidadCampañas } = opciones;

      // Generar productos
      const resultadoProductos = await this.generarProductosEmpresa(empresa, cantidadProductos);

      // Pequeña pausa entre operaciones
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generar campañas
      const resultadoCampañas = await this.generarCampañasEmpresa(empresa, cantidadCampañas);

      return {
        exito: resultadoProductos.exito && resultadoCampañas.exito,
        productosCreados: resultadoProductos.productosCreados,
        campañasCreadas: resultadoCampañas.campañasCreadas,
        errores: [...resultadoProductos.errores, ...resultadoCampañas.errores]
      };
    } catch (error) {
      console.error('Error generando datos de prueba:', error);
      return {
        exito: false,
        productosCreados: 0,
        campañasCreadas: 0,
        errores: [error.message]
      };
    }
  }

  /**
   * Asignar marcas comunes a una empresa si no las tiene
   * @param {Object} empresa - Datos de la empresa
   * @returns {Array} Array de marcas asignadas
   */
  static asignarMarcasComunes(empresa) {
    // Si ya tiene marcas, usarlas
    if (empresa.marcas && Array.isArray(empresa.marcas) && empresa.marcas.length > 0) {
      return empresa.marcas;
    }

    // Si no tiene marcas, asignar 2-4 marcas aleatorias
    const cantidadMarcas = Math.floor(Math.random() * 3) + 2; // 2-4 marcas
    const marcasAleatorias = [...this.marcasComunes]
      .sort(() => Math.random() - 0.5)
      .slice(0, cantidadMarcas);
    
    return marcasAleatorias;
  }

  /**
   * Generar campañas y productos sugeridos para múltiples empresas con marcas comunes
   * Esta función crea datos de ejemplo que aparecerán en el dashboard del cliente
   * @param {Object} opciones - Opciones de generación
   * @param {number} opciones.maxEmpresas - Número máximo de empresas a procesar (por defecto 10)
   * @param {number} opciones.productosPorEmpresa - Productos a generar por empresa (por defecto 3-5)
   * @param {number} opciones.campanasPorEmpresa - Campañas a generar por empresa (por defecto 1-2)
   * @returns {Promise<Object>} Resultado con estadísticas de generación
   */
  static async generarCampañasYProductosSugeridos(opciones = {}) {
    try {
      const { 
        maxEmpresas = 10,
        productosPorEmpresa = null,
        campanasPorEmpresa = null
      } = opciones;

      console.log('🔄 Iniciando generación de campañas y productos sugeridos...');

      // Obtener empresas activas o validadas
      const empresasQuery = query(
        collection(db, 'empresas'),
        where('estado', 'in', [ESTADOS_EMPRESA.ACTIVA, ESTADOS_EMPRESA.VALIDADA])
      );

      const empresasSnapshot = await getDocs(empresasQuery);
      const todasLasEmpresas = empresasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log(`📊 Empresas activas/validadas encontradas: ${todasLasEmpresas.length}`);

      // Limitar cantidad de empresas a procesar
      const empresasAProcesar = todasLasEmpresas.slice(0, maxEmpresas);
      console.log(`🎯 Procesando ${empresasAProcesar.length} empresas...`);

      let totalProductosCreados = 0;
      let totalCampanasCreadas = 0;
      const errores = [];
      const empresasProcesadas = [];

      for (const empresa of empresasAProcesar) {
        try {
          // Asignar marcas comunes si la empresa no las tiene
          const marcasAsignadas = this.asignarMarcasComunes(empresa);
          
          // Crear empresa con marcas asignadas para generar datos
          const empresaConMarcas = {
            ...empresa,
            marcas: marcasAsignadas
          };

          console.log(`📦 Procesando empresa: ${empresa.nombre || empresa.id} - Marcas: ${marcasAsignadas.join(', ')}`);

          // Generar productos y campañas
          const resultado = await this.generarDatosPruebaEmpresa(empresaConMarcas, {
            cantidadProductos: productosPorEmpresa,
            cantidadCampañas: campanasPorEmpresa
          });

          totalProductosCreados += resultado.productosCreados;
          totalCampanasCreadas += resultado.campañasCreadas;
          errores.push(...resultado.errores);

          empresasProcesadas.push({
            empresa: empresa.nombre || empresa.id,
            productos: resultado.productosCreados,
            campañas: resultado.campañasCreadas,
            marcas: marcasAsignadas
          });

          // Pausa entre empresas para no saturar Firestore
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (error) {
          console.error(`❌ Error procesando empresa ${empresa.nombre || empresa.id}:`, error);
          errores.push(`Error en empresa ${empresa.nombre || empresa.id}: ${error.message}`);
        }
      }

      const resultadoFinal = {
        exito: errores.length === 0 || (totalProductosCreados > 0 || totalCampanasCreadas > 0),
        totalEmpresasProcesadas: empresasProcesadas.length,
        totalProductosCreados,
        totalCampanasCreadas,
        errores,
        empresasProcesadas
      };

      console.log('✅ Generación completada:', {
        empresas: resultadoFinal.totalEmpresasProcesadas,
        productos: resultadoFinal.totalProductosCreados,
        campañas: resultadoFinal.totalCampanasCreadas,
        errores: resultadoFinal.errores.length
      });

      return resultadoFinal;
    } catch (error) {
      console.error('❌ Error generando campañas y productos sugeridos:', error);
      return {
        exito: false,
        totalEmpresasProcesadas: 0,
        totalProductosCreados: 0,
        totalCampanasCreadas: 0,
        errores: [error.message],
        empresasProcesadas: []
      };
    }
  }
}

export default GenerarDatosPruebaService;

